export const IMPORT_VARIANTS = ['raw-video', 'aligned-video', 'raw-audio', 'screen', 'screen-aligned', 'mix', 'transcript', 'other'] as const
export type ImportVariant = typeof IMPORT_VARIANTS[number]
const tokens: [string, ImportVariant][] = [
  ['screenshare_hd_video_screenshare', 'screen'],
  ['screen_share_aligned', 'screen-aligned'], ['screen-share-aligned', 'screen-aligned'], ['aligned_screen_share', 'screen-aligned'],
  ['screen_share', 'screen'], ['screen-share', 'screen'], ['screen_aligned', 'screen-aligned'],
  ['aligned_video', 'aligned-video'], ['video_aligned', 'aligned-video'], ['aligned-video', 'aligned-video'],
  ['raw-video-cfr', 'raw-video'], ['raw_video_cfr', 'raw-video'],
  ['raw_video', 'raw-video'], ['raw-video', 'raw-video'], ['raw_audio', 'raw-audio'], ['raw-audio', 'raw-audio'],
  ['video', 'raw-video'], ['audio', 'raw-audio'], ['screen', 'screen'], ['transcript', 'transcript'], ['mix', 'mix'],
]

/** Match a known capture token, leaving underscores in both the person and project intact. */
export function parseRiversideName(name: string): { participant: string; variant: ImportVariant; project: string; id: string } | null {
  const stem = name.replace(/\.[^.]+$/, '')
  const aligned = /^(.+?)-([0-9a-f]{8}(?:-[0-9a-f]{4}){3}(?:-[0-9a-f]{12})?)-[0-9]+-CFR$/i.exec(stem)
  if (aligned) return { participant: aligned[1]!.toLowerCase() === 'screenshare' ? 'Screen shares' : aligned[1]!.replace(/_/g, ' '), variant: aligned[1]!.toLowerCase() === 'screenshare' ? 'screen-aligned' : 'aligned-video', project: '', id: aligned[2]! }
  if (!stem.toLowerCase().startsWith('riverside_')) return null
  const body = stem.slice(10)
  if (body.toLowerCase().startsWith('screenshare_hd_video_screenshare_')) {
    const tail = body.slice('screenshare_hd_video_screenshare_'.length)
    const at = tail.lastIndexOf('_')
    if (at > 0) return { participant: 'Screen shares', variant: 'screen', project: tail.slice(0, at).replace(/_/g, ' '), id: tail.slice(at + 1) }
  }
  for (const [token, variant] of tokens) {
    const marker = `_${token}_`
    const at = body.toLowerCase().indexOf(marker)
    if (at < 1) continue
    const tail = body.slice(at + marker.length)
    const end = tail.lastIndexOf('_')
    if (end < 1 || end === tail.length - 1) continue
    const participant = body.slice(0, at).replace(/_/g, ' ')
    return { participant, variant: /^all[ _-]participants$/i.test(participant) ? 'mix' : variant, project: tail.slice(0, end).replace(/_/g, ' '), id: tail.slice(end + 1) }
  }
  return null
}

export interface MappingInput { id: string; name: string; probe?: { durationMs: number } | null }
export interface ImportMapping { id: string; participantName: string | null; variant: ImportVariant; role: 'source' | 'reference' | 'original'; sourceKey: string | null; referenceFileId: string | null }
/** Find a complete assignment without enumerating permutations; an excluded edge tests necessity. */
function screenAssignment(options: number[][], excludedSlot = -1, excludedRaw = -1): number[] | null {
  const byRaw = new Array<number>(options.length).fill(-1)
  function visit(slot: number, seen: Set<number>): boolean {
    for (const raw of options[slot]!) {
      if (seen.has(raw) || slot === excludedSlot && raw === excludedRaw) continue
      seen.add(raw)
      if (byRaw[raw] === -1 || visit(byRaw[raw]!, seen)) { byRaw[raw] = slot; return true }
    }
    return false
  }
  for (let slot = 0; slot < options.length; slot++) if (!visit(slot, new Set())) return null
  const bySlot = new Array<number>(options.length)
  byRaw.forEach((slot, raw) => { bySlot[slot] = raw })
  return bySlot
}

export function autoMapping(files: MappingInput[]): ImportMapping[] {
  const screens = new Map<string, number>()
  const people = new Map<string, string>()
  for (const f of [...files].sort((a, b) => a.name.localeCompare(b.name))) {
    const parsed = parseRiversideName(f.name)
    if (/^riverside_/i.test(f.name) && parsed && !['mix', 'screen', 'screen-aligned'].includes(parsed.variant)) people.set(parsed.participant.toLowerCase(), people.get(parsed.participant.toLowerCase()) ?? parsed.participant)
  }
  const result = [...files].sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }) || a.id.localeCompare(b.id)).map((f): ImportMapping => {
    const parsed = parseRiversideName(f.name)
    if (!parsed) return { id: f.id, participantName: null, variant: 'other', role: 'original', sourceKey: null, referenceFileId: null }
    const { variant } = parsed
    const participant = people.get(parsed.participant.toLowerCase()) ?? parsed.participant
    if (!/^riverside_/i.test(f.name) && variant === 'aligned-video' && !people.has(participant.toLowerCase())) return { id: f.id, participantName: null, variant, role: 'original', sourceKey: null, referenceFileId: null }
    const media = /\.(mp4|mov|mkv|webm|wav|m4a|flac|mp3)$/i.test(f.name)
    let sourceKey: string | null = variant === 'mix' ? 'mix' : variant === 'raw-audio' ? 'mic' : variant.includes('video') ? 'camera' : null
    if (variant === 'screen' || variant === 'screen-aligned') {
      const k = `${participant}:${variant}`
      const n = (screens.get(k) ?? 0) + 1
      screens.set(k, n)
      sourceKey = n <= 99 ? `screen-${n}` : null
    }
    return { id: f.id, participantName: variant === 'mix' ? 'All participants' : participant, variant, role: media && sourceKey ? 'source' : 'original', sourceKey, referenceFileId: null }
  })
  for (const aligned of result.filter((f) => f.variant === 'aligned-video' || f.variant === 'screen-aligned')) {
    const candidates = result.filter((f) => f.participantName === aligned.participantName && f.sourceKey === aligned.sourceKey && f.id !== aligned.id && (f.variant === 'raw-video' || f.variant === 'screen'))
    const raw = candidates[0]
    if (raw) { aligned.role = 'reference'; raw.referenceFileId = aligned.id }
  }
  // Riverside's standalone aligned screens end with the capture, with black only before it.
  // A preview is paired only if every feasible non-overlapping assignment agrees. Actual black
  // spans at commit decide the final pairing, including captures that overlap or have padded ends.
  for (const participant of new Set(result.filter((f) => f.variant === 'screen').map((f) => f.participantName))) {
    const raw = result.filter((f) => f.participantName === participant && f.variant === 'screen')
    const aligned = result.filter((f) => f.participantName === participant && f.variant === 'screen-aligned')
    const duration = (id: string) => files.find((f) => f.id === id)?.probe?.durationMs
    if (!raw.length || raw.length !== aligned.length || [...raw, ...aligned].some((f) => !duration(f.id))) continue
    const ordered = [...aligned].sort((a, b) => duration(a.id)! - duration(b.id)!)
    const options = ordered.map((ref, slot) => {
      const capacity = duration(ref.id)! - (slot ? duration(ordered[slot - 1]!.id)! : 0)
      return raw.flatMap((capture, index) => duration(capture.id)! <= capacity + 40 ? [index] : [])
    })
    const match = new Set(ordered.map((f) => duration(f.id))).size === ordered.length ? screenAssignment(options) : null
    raw.forEach((capture) => { capture.referenceFileId = null })
    ordered.forEach((ref, slot) => { ref.role = 'reference'; ref.sourceKey = `screen-${slot + 1}` })
    const used = new Set<string>()
    match?.forEach((rawIndex, slot) => {
      // If deleting this edge still admits a complete matching, its identity is ambiguous.
      if (screenAssignment(options, slot, rawIndex)) return
      const capture = raw[rawIndex]!, ref = ordered[slot]!
      capture.sourceKey = ref.sourceKey; capture.referenceFileId = ref.id
      used.add(capture.sourceKey!)
    })
    let next = 1
    for (const capture of raw.filter((f) => !f.referenceFileId)) {
      while (used.has(`screen-${next}`)) next++
      capture.sourceKey = `screen-${next++}`
    }
  }
  return files.map((f) => result.find((m) => m.id === f.id)!)
}

/** Project-less aligned siblings inherit the one Riverside project represented by the selection. */
export function importTitle(files: { name: string }[]): string {
  if (!files.length) return ''
  const input = files.map((f, index) => ({ name: f.name, id: String(index) }))
  const mapping = autoMapping(input)
  const projects = files.map((f) => parseRiversideName(f.name)?.project.trim().toLowerCase()).filter((p): p is string => !!p)
  const common = new Set(projects)
  if (common.size === 1 && files.every((f, index) => !!parseRiversideName(f.name)?.project || ['aligned-video', 'screen-aligned'].includes(mapping[index]!.variant) && mapping[index]!.role !== 'original')) {
    return projects[0]!.split(/[\s_]+/).map((p) => p[0]!.toUpperCase() + p.slice(1)).join(' ')
  }
  return files[0]!.name.replace(/\.[^.]+$/, '')
}
